import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface EditorSettings {
  smoothCaret: boolean;
  pomodoroEnabled: boolean;
  pomodoroMinutes: number;
  pomodoroBreakMinutes: number;
  plainTextPaste: boolean;
  typingSoundEnabled: boolean;
  typingSoundVolume: number;
  saveSoundEnabled: boolean;
  saveSoundVolume: number;
  blurOnUnfocus: boolean;
  liveKeyboard: boolean;
  spotifyEnabled: boolean;
}

export const EDITOR_SETTINGS_KEY = 'editor-settings-v1';

const DEFAULTS: EditorSettings = {
  smoothCaret: true,
  pomodoroEnabled: false,
  pomodoroMinutes: 25,
  pomodoroBreakMinutes: 5,
  plainTextPaste: true,
  typingSoundEnabled: true,
  typingSoundVolume: 0.22,
  saveSoundEnabled: true,
  saveSoundVolume: 0.18,
  blurOnUnfocus: false,
  liveKeyboard: false,
  spotifyEnabled: false,
};

function load(): EditorSettings {
  if (typeof window === 'undefined') return { ...DEFAULTS };
  try {
    const raw = localStorage.getItem(EDITOR_SETTINGS_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw);
    const result = { ...DEFAULTS, ...parsed };
    try {
      const tE = localStorage.getItem('editor-typing-sound-enabled');
      const sE = localStorage.getItem('editor-save-sound-enabled');
      const tV = localStorage.getItem('editor-typing-sound-volume');
      const sV = localStorage.getItem('editor-save-sound-volume');
      if (tE != null && parsed.typingSoundEnabled === undefined) result.typingSoundEnabled = tE === 'true';
      if (sE != null && parsed.saveSoundEnabled === undefined) result.saveSoundEnabled = sE === 'true';
      if (tV != null && parsed.typingSoundVolume === undefined) result.typingSoundVolume = Math.min(1, Math.max(0, Number(tV) || 0.22));
      if (sV != null && parsed.saveSoundVolume === undefined) result.saveSoundVolume = Math.min(1, Math.max(0, Number(sV) || 0.18));
    } catch {}
    return result;
  } catch {
    return { ...DEFAULTS };
  }
}

function save_local(s: EditorSettings) {
  try {
    localStorage.setItem(EDITOR_SETTINGS_KEY, JSON.stringify(s));
  } catch {}
}

/** Dispatch a storage event so other hook instances pick up changes. */
export function dispatchSettingsSync() {
  window.dispatchEvent(new StorageEvent('storage', { key: EDITOR_SETTINGS_KEY }));
}

/** Salva no Supabase (debounced internamente pelo chamador) */
async function save_remote(user_id: string, s: EditorSettings) {
  await supabase
    .from('profiles')
    .update({ editor_settings: s as unknown as Record<string, unknown> } as Record<string, unknown>)
    .eq('id', user_id);
}

export function useEditorSettings() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<EditorSettings>(load);

  /** Carrega do Supabase ao montar */
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;

    const load_remote = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('editor_settings')
        .eq('id', user.id)
        .single();

      if (!cancelled && data) {
        const remote = (data as Record<string, unknown>).editor_settings;
        if (remote && typeof remote === 'object' && Object.keys(remote as object).length > 0) {
          const merged = { ...DEFAULTS, ...(remote as Partial<EditorSettings>) };
          setSettings(merged);
          save_local(merged);
        }
      }
    };

    load_remote();
    return () => { cancelled = true; };
  }, [user?.id]);

  /** Salva localmente a cada mudança */
  useEffect(() => {
    save_local(settings);
  }, [settings]);

  /** Sincroniza entre abas */
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === EDITOR_SETTINGS_KEY || e.key === null) {
        setSettings(load());
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const update = useCallback(<K extends keyof EditorSettings>(key: K, value: EditorSettings[K]) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      save_local(next);
      dispatchSettingsSync();

      // Salva no Supabase
      if (user?.id) {
        save_remote(user.id, next);
      }

      return next;
    });
  }, [user?.id]);

  return { settings, update, setSettings };
}
